import { Box } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { signInSession, getToken, setTemporaryToken } from '@/services/auth.service';
import { checkOpenState } from '@/services/open-mall.service';
import { dialogOpen } from '@/modules/MuiDialog';
import ResetPasswordDialog from './SodomallPartnerLogin/ResetPasswordDialog';
import AuthForm from '@/components/auth/AuthForm';

import sodomallPartnerLoginBg from '@/assets/images/page/sodomall_partner_login.jpg';

/** 소도몰 파트너 브랜드 컬러 — 앱 기본 primary(소듀 핑크)와 다르다 */
const SODOMALL_ACCENT = '#363ac3';

/**
 * 소도몰 파트너 로그인 — 로그인 페이지와 같은 2단 레이아웃.
 * 좌측 패널만 소도몰 브랜드 이미지로 바뀌고, 폼 본문은 `AuthForm` 을 그대로 공유한다.
 * 소셜 로그인과 회원가입 안내는 노출하지 않는다(파트너 계정은 초대로 생성된다).
 */
export default (props: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [queries] = useSearchParams();

    const refer = queries.get('refer');
    const defaultEmail = queries.get('email') || '';

    const [isResetOpen, setIsResetOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');

    const signIn = async (email: string, password: string) => {
        try {
            const session = await signInSession(email, password);

            toast.success(t('auth.loginSuccess'));

            if (!refer) {
                navigate(`/studio/${session.userId}`);
            } else {
                navigate(refer);
            }
        } catch (e: any) {
            console.error(e);

            if (!!e.data?.placeHolder || !!e.placeHolder) {
                toast.error(e.data?.placeHolder || e.placeHolder);
                return;
            }

            if (e.status === 302) {
                toast.error(t('auth.accountDeactivated'));
                return;
            }

            if (e.code === 'not-exist-own-mall') {
                const { userId, userSecret } = e.data?.signInInfo || {};
                const { accessToken } = await getToken(userId, userSecret);

                if (!accessToken) return;
                const state = await checkOpenState(accessToken);

                if (!state) {
                    dialogOpen({
                        title: '아직 상점을 개설하지 않으셨네요!',
                        message: '본인만의 상점을 개설하시겠어요?',
                        confirmText: '지금 신청하러 가기',
                        onConfirmed: async () => {
                            try {
                                setTemporaryToken(accessToken);
                                navigate('/register', { state: e.data?.signInInfo });
                            } catch (err: any) {
                                if (err.data?.placeHolder || err.placeHolder) {
                                    toast.error(err.data?.placeHolder || err.placeHolder);
                                } else {
                                    console.error(err);
                                }
                            }
                        },
                        closeText: '나중에 할게요',
                        closeButtonProps: { color: 'error' },
                    });
                } else {
                    setTemporaryToken(accessToken);
                    navigate('/register');
                }

                return;
            }

            toast.error(t('auth.loginFailed'));
        }
    };

    return (
        <>
            <Helmet titleTemplate={`%s ${t('auth.login')}`} />
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                }}
            >
                {/* 좌측 — 소도몰 파트너 브랜드 이미지 (데스크톱 전용) */}
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'none', lg: 'block' },
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: SODOMALL_ACCENT,
                    }}
                >
                    <Box
                        component="img"
                        src={sodomallPartnerLoginBg}
                        alt=""
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 2.5, lg: 4 },
                        py: { xs: 5, lg: 4 },
                    }}
                >
                    <AuthForm
                        refer={refer}
                        defaultEmail={defaultEmail}
                        onSubmitCredentials={signIn}
                        showSocial={false}
                        showSignUpLink={false}
                        accentColor={SODOMALL_ACCENT}
                        subtitle="파트너 계정으로 로그인하세요."
                        title={
                            <Box
                                component="img"
                                src="/sodomall_logo.png"
                                alt="소도몰"
                                sx={{ display: 'block', height: 40, width: 'auto', objectFit: 'contain' }}
                            />
                        }
                        onForgotPassword={(email) => {
                            setResetEmail(email);
                            setIsResetOpen(true);
                        }}
                    />
                </Box>
            </Box>

            <ResetPasswordDialog open={isResetOpen} onClose={() => setIsResetOpen(false)} email={resetEmail} />
        </>
    );
};
